import React, { Component } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import swal from "sweetalert2";
import TableContainerWithLocation from "../Components/TableContainerWithLocation";
import recovery from "@/api/recovery";
import { dateFormatter } from "@/util/formatter";
import { handleError } from "@/util/errorHandler";
import { Link } from "react-router-dom";

const STATUSES = [
  {
    value: "ALL",
    label: "All",
  },
  {
    value: "PREPARED",
    label: "Prepared",
  },
  {
    value: "VERIFIED",
    label: "Verified",
  },
  {
    value: "OPENED",
    label: "Opened",
  },
  {
    value: "READY",
    label: "Ready",
  },
  {
    value: "AUTHORIZED",
    label: "Authorized",
  },
  {
    value: "SENT",
    label: "Sent",
  },
  {
    value: "REVOKED",
    label: "Revoked",
  },
  {
    value: "APPROVED",
    label: "Approved",
  },
  {
    value: "REJECTED",
    label: "Rejected",
  },
  {
    value: "FAILED",
    label: "Failed",
  },
  {
    value: "SUCCESSFUL",
    label: "Successful",
  },
  {
    value: "CANCELED",
    label: "Canceled",
  },
];

class RecoveryRequests extends Component {
  state = {
    selectedStatus: "READY",
  };

  renderActionButton = (cell, row) => {
    const { id } = row;

    return row.status === "READY" ? (
      <>
        <Button
          color="danger"
          type="button"
          onClick={() => this.askRejectRequest(id)}>
          Reject
        </Button>
        <Button
          color="success"
          type="button"
          onClick={() => this.askApproveRequest(id)}>
          Approve
        </Button>
      </>
    ) : (
      "No actions"
    );
  };

  askRejectRequest = async id => {
    const html = `
      <span>
        Do you want to reject this request?
      </span>
      <br/>
    `;

    await swal({
      title: `Reject request?`,
      html,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          await recovery.rejectRecoveryRequest(id);
          this.table && this.table.loadAndStoreData();
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  askApproveRequest = async id => {
    const html = `
      <span>
        Do you want to approve this request?
      </span>
    `;

    await swal({
      title: `Approve request?`,
      html,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async reason => {
        try {
          await recovery.approveRecoveryRequest(id);
          this.table && this.table.loadAndStoreData();
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  table = null;

  bindInnerTableRef = innerComponent => {
    this.table = innerComponent;
  };

  handleStatusChange = event => {
    this.setState(
      {
        selectedStatus: event.target.value,
      },
      () => {
        this.table && this.table.loadAndStoreData();
      },
    );
  };

  loadPagination = async (page = 0, size = 10, search, sort) => {
    const status =
      this.state.selectedStatus === "ALL"
        ? undefined
        : this.state.selectedStatus;
    let result = await recovery.getRecoveryRequestsList(
      page,
      size,
      search,
      sort,
      status,
    );
    return result;
  };

  viewFormatter = (cell, row) => (
    <Link
      to={{
        pathname: `/recovery-requests-info/${row.id}`,
        state: { data: row },
      }}>
      <div>
        <i className="fa fa-search" style={{ color: "#D15050" }} />
      </div>
    </Link>
  );

  render() {
    return (
      <Card>
        <CardHeader>Key recovery requests list</CardHeader>
        <CardBody>
          <Form
            inline
            style={{
              marginBottom: ".5rem",
              justifyContent: "flex-end",
            }}>
            <FormGroup
              className="mb-2 mr-sm-2 mb-sm-0"
              style={{ paddingRight: "2rem", alignItems: "baseline" }}>
              <Label htmlFor="type" style={{ marginRight: "1rem" }}>
                <h6>Status:</h6>
              </Label>
              <Input
                type="select"
                name="status"
                id="status"
                value={this.state.selectedStatus}
                style={{
                  maxWidth: "300px",
                }}
                onChange={this.handleStatusChange}>
                {STATUSES.map(type => {
                  return (
                    <option value={type.value} key={type.value}>
                      {type.label}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
          </Form>
          <TableContainerWithLocation
            loadData={this.loadPagination}
            options={{
              defaultSortName: "date", // to change default sort order also change default arguments in API recovery.fetchRecoveryRequestsList
              defaultSortOrder: "desc",
            }}
            wrappedComponentRef={this.bindInnerTableRef}>
            <BootstrapTable
              version="4"
              search
              pagination
              striped
              hover
              condensed
              remote
              keyField="id">
              <TableHeaderColumn
                dataFormat={this.viewFormatter}
                dataField="id"
                width="60"
                dataAlign="center">
                View
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="status"
                dataAlign="center"
                width="100">
                Status
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="accountId" width="150">
                Account ID
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="name" width="160">
                Name
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={trxHash => trxHash || "not created"}
                dataField="trxHash"
                width="100">
                Transaction hash
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={trxStatus => trxStatus || "not created"}
                dataField="trxStatus"
                width="100">
                TX status
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={dateFormatter}
                dataField="date"
                width="160">
                Registered Date
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={dateFormatter}
                dataField="updateTime"
                width="130">
                Last update
              </TableHeaderColumn>
              <TableHeaderColumn width="100" dataField="comment">
                Reason
              </TableHeaderColumn>
              <TableHeaderColumn
                dataFormat={this.renderActionButton}
                width="160"
                dataAlign="center">
                Actions
              </TableHeaderColumn>
            </BootstrapTable>
          </TableContainerWithLocation>
        </CardBody>
      </Card>
    );
  }
}

export default RecoveryRequests;
